<template>
  <v-app id="inspire">
    <v-app-bar
      app
      color="dark"
      dark
    >
    <v-toolbar-title>Choose a Graph to Label</v-toolbar-title>
    <v-spacer></v-spacer>
    <v-tooltip bottom>
    <template v-slot:activator="{ on, attrs }">
    <v-btn
            class="ma-2"
            large
            color="dark"
            v-bind="attrs" v-on="on"
            @click="go_back"
            >
            <v-icon>mdi-arrow-left</v-icon>
            Back to Login
    </v-btn>
    </template>
    <span style="font-size:100%">Go back to login page</span>
    </v-tooltip>
    </v-app-bar>
    
    <v-main>
      <v-container>
        <v-row>
          <v-col cols="12">
            <v-card>
              <v-card-title class="text-h5">
                Select a Graph to Label
                <v-spacer></v-spacer>
                <v-chip color="primary" large>
                  {{ unlabeled_graphs.length }} graphs available
                </v-chip>
              </v-card-title>
              <v-card-text>
                <p class="text-body-1 mb-4">
                  Choose any graph from the list below that you haven't labeled yet. 
                  Each graph will show you 1-10 questions to answer.
                </p>
                
                <!-- Search and Filter -->
                <v-row class="mb-4">
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="search"
                      label="Search by Graph ID"
                      prepend-inner-icon="mdi-magnify"
                      clearable
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="sortBy"
                      :items="sortOptions"
                      label="Sort by"
                      @change="sortGraphs"
                    ></v-select>
                  </v-col>
                </v-row>
                
                <!-- Graph Grid -->
                <v-row v-if="filteredGraphs.length > 0">
                  <v-col
                    v-for="graph_id in paginatedGraphs"
                    :key="graph_id"
                    cols="12"
                    sm="6"
                    md="4"
                    lg="3"
                  >
                    <v-card
                      class="graph-card"
                      hover
                      @click="selectGraph(graph_id)"
                    >
                      <v-img
                        :src="getGraphImageUrl(graph_id)"
                        aspect-ratio="1.5"
                        height="200"
                        class="grey lighten-2"
                      >
                        <template v-slot:placeholder>
                          <v-row
                            class="fill-height ma-0"
                            align="center"
                            justify="center"
                          >
                            <v-progress-circular
                              indeterminate
                              color="grey lighten-5"
                            ></v-progress-circular>
                          </v-row>
                        </template>
                        <v-overlay
                          absolute
                          :value="selectedGraph === graph_id"
                          color="primary"
                          opacity="0.8"
                        >
                          <v-icon size="48" color="white">mdi-check-circle</v-icon>
                        </v-overlay>
                      </v-img>
                      <v-card-title class="text-center">
                        Graph #{{ graph_id }}
                      </v-card-title>
                      <v-card-actions class="justify-center">
                        <v-btn
                          color="primary"
                          @click.stop="selectGraph(graph_id)"
                        >
                          Label This Graph
                        </v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-col>
                </v-row>
                
                <!-- No graphs found -->
                <v-row v-else>
                  <v-col cols="12" class="text-center">
                    <v-card>
                      <v-card-text>
                        <v-icon size="64" color="grey">mdi-graph-off</v-icon>
                        <h3 class="mt-4">No graphs found</h3>
                        <p>Try adjusting your search criteria.</p>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
                
                <!-- Pagination -->
                <v-row v-if="totalPages > 1">
                  <v-col cols="12" class="text-center">
                    <v-pagination
                      v-model="currentPage"
                      :length="totalPages"
                      :total-visible="7"
                    ></v-pagination>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
export default {
  name: 'GraphSelector',
  data() {
    return {
      unlabeled_graphs: [],
      search: '',
      sortBy: 'id',
      selectedGraph: null,
      currentPage: 1,
      itemsPerPage: 12,
      sortOptions: [
        { text: 'Graph ID (Ascending)', value: 'id' },
        { text: 'Graph ID (Descending)', value: 'id_desc' },
        { text: 'Random', value: 'random' }
      ]
    }
  },
  computed: {
    filteredGraphs() {
      let filtered = this.unlabeled_graphs;
      
      // Apply search filter
      if (this.search) {
        filtered = filtered.filter(id => 
          id.toString().includes(this.search)
        );
      }
      
      return filtered;
    },
    totalPages() {
      return Math.ceil(this.filteredGraphs.length / this.itemsPerPage);
    },
    paginatedGraphs() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredGraphs.slice(start, end);
    }
  },
  created() {
    this.loadUnlabeledGraphs();
  },
  methods: {
    loadUnlabeledGraphs() {
      const username = this.$route.params.username;
      fetch(`http://localhost:5000/get_unlabeled_graphs/${username}/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json"
        }
      })
      .then(resp => resp.json())
      .then(data => {
        this.unlabeled_graphs = data.unlabeled_graphs;
        console.log(`Loaded ${data.unlabeled_graphs.length} unlabeled graphs`);
      })
      .catch(err => {
        console.error('Error loading unlabeled graphs:', err);
        this.$router.push({ path: '/' });
      });
    },
    
    getGraphImageUrl(graph_id) {
      return `https://gan-layout.s3.us-east-2.amazonaws.com/rome/${graph_id}/neato.png`;
    },
    
    selectGraph(graph_id) {
      this.selectedGraph = graph_id;
      // Navigate to question labeling for this graph
      const user_id = this.$route.params.userid;
      const username = this.$route.params.username;
      this.$router.push({
        path: `/question-labelling/${user_id}/${username}/${graph_id}`
      });
    },
    
    sortGraphs() {
      if (this.sortBy === 'id') {
        this.unlabeled_graphs.sort((a, b) => a - b);
      } else if (this.sortBy === 'id_desc') {
        this.unlabeled_graphs.sort((a, b) => b - a);
      } else if (this.sortBy === 'random') {
        // Fisher-Yates shuffle
        for (let i = this.unlabeled_graphs.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [this.unlabeled_graphs[i], this.unlabeled_graphs[j]] = [this.unlabeled_graphs[j], this.unlabeled_graphs[i]];
        }
      }
      this.currentPage = 1; // Reset to first page
    },
    
    go_back() {
      this.$router.push({ path: '/' });
    }
  }
}
</script>

<style scoped>
.graph-card {
  cursor: pointer;
  transition: transform 0.2s;
}

.graph-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
}
</style>
